import React, { useState } from 'react';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Modal } from '../../../components/Modal';

interface ClaimQuantityModalProps {
    isOpen: boolean;
    onClose: () => void;
    itemName: string;
    currentClaim: number;
    maxAvailable: number; // max additional they can claim
    itemPrice: number;
    onConfirm: (quantity: number) => void;
}

export const ClaimQuantityModal: React.FC<ClaimQuantityModalProps> = ({
    isOpen,
    onClose,
    itemName,
    currentClaim,
    maxAvailable,
    itemPrice,
    onConfirm
}) => {
    // Initialize state from prop. 
    // We rely on parent to use key={item.id} (or similar) to reset state when opening for a new item/context.
    // OR we can use a simpler approach: if local state is needed, we accept we might need to sync it.
    // But since we fixed the parent to use key, this is safe.
    const [quantity, setQuantity] = useState(currentClaim.toString());

    const parsedQty = parseInt(quantity) || 0;
    // user wants to set total claim to `parsedQty`.
    // Valid range: 0 to (currentClaim + maxAvailable)
    // Because maxAvailable = (Total - Others).
    // So my Max = (Total - Others).
    // Wait, maxAvailable passed from parent is (Total - Others).
    // So if I have 2, and maxAvailable is 3 (5 total - 0 others? no, 5 total - 2 me = 3 left?).
    // Parent logic: maxAvailable = (qty - othersClaims).
    // So valid range for ME is 0 to maxAvailable.
    const maxVal = maxAvailable;
    const isValid = parsedQty >= 0 && parsedQty <= maxVal;

    const handleConfirm = () => {
        const val = parseInt(quantity);
        if (!isNaN(val) && val >= 0 && val <= maxVal) {
            onConfirm(val);
            onClose();
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Update Your Share">
            <div className="space-y-4">
                <p className="text-sm text-text-muted">
                    How many <b>{itemName}</b> did you have?
                </p>
                <div className="flex gap-4 items-center">
                    <Button
                        variant="secondary"
                        onClick={() => { setQuantity(Math.max(0, parsedQty - 1).toString()); }}
                    >-</Button>
                    <div className="flex-1">
                        <Input
                            type="number"
                            min="0"
                            max={maxVal}
                            value={quantity}
                            onChange={(e) => { setQuantity(e.target.value); }}
                            className="text-center font-bold text-lg"
                            autoFocus
                        />
                    </div>
                    <Button
                        variant="secondary"
                        onClick={() => { setQuantity(Math.min(maxVal, parsedQty + 1).toString()); }}
                    >+</Button>
                </div>

                <div className="bg-bg-surface p-3 rounded-lg flex justify-between items-center">
                    <span className="text-sm text-text-muted">Total Cost</span>
                    <span className="font-bold text-text-heading">
                        ${(parsedQty * itemPrice).toFixed(2)}
                    </span>
                </div>

                <div className="flex gap-2 pt-2">
                    <Button variant="ghost" className="flex-1" onClick={onClose}>Cancel</Button>
                    <Button className="flex-1" onClick={handleConfirm} disabled={!isValid}>
                        Update
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
